import axios from "axios";
import React, { useEffect, useState, useCallback } from "react";
import ApiUrl from "../Environment/ApiUrl";
import { CSVLink } from "react-csv";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import debounce from "lodash/debounce";

function Report() {
  const [selectedReport, setSelectedReport] = useState("DailyRates Report");

  // ── Data states ────────────────────────────────────────────────────────────
  const [dailyRatesData, setDailyRatesData] = useState([]);
  const [purchaseData, setPurchaseData] = useState([]);
  const [retailerData, setRetailerData] = useState([]);
  const [ordersData, setOrdersData] = useState([]);
  const [salesData, setSalesData] = useState([]);
  const [chitListData, setChitListData] = useState([]);
  const [productsData, setProductsData] = useState([]);
  const [customersData, setCustomersData] = useState([]);
  const [suppliersData, setSuppliersData] = useState([]);
  const [loansData, setLoansData] = useState([]);
  const [stockData, setStockData] = useState([]);

  // ── Pagination ─────────────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showAllItems, setShowAllItems] = useState(false);
  const [filteredData, setFilteredData] = useState([]);

  // ── Search & date ──────────────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // ── Advanced filters ───────────────────────────────────────────────────────
  const [selectedManager, setSelectedManager] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedSupplier, setSelectedSupplier] = useState("");
  const [selectedRetailer, setSelectedRetailer] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("");

  const debouncedSetSearch = useCallback(debounce((v) => setSearchTerm(v), 300), []);

  // ── Fetch on report change ─────────────────────────────────────────────────
  useEffect(() => {
    clearFilters(false);
    const fetchers = {
      "DailyRates Report": fetchDailyRates,
      "Purchases Report": fetchPurchases,
      "Retailers Report": fetchRetailers,
      "Orders Report": fetchOrders,
      "Sales Report": fetchSales,
      "Chit Report": fetchChitList,
      "Products Report": fetchProducts,
      "Customers Report": fetchCustomers,
      "Suppliers Report": fetchSuppliers,
      "Loans Report": fetchLoans,
      "Stock Report": fetchStock,
    };
    fetchers[selectedReport]?.();
  }, [selectedReport]);

  useEffect(() => { 
    applyFilters(); 
  }, [
    searchTerm, fromDate, toDate,
    selectedManager, selectedStaff, selectedStatus,
    selectedCustomer, selectedSupplier, selectedRetailer, paymentFilter,
    dailyRatesData, purchaseData, retailerData, ordersData,
    salesData, chitListData, productsData, customersData,
    suppliersData, loansData, stockData,
  ]);

  // ── Fetch functions ────────────────────────────────────────────────────────
  const fetchDailyRates = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/daily_rates.php`); setDailyRatesData(r.data?.data || []); }
    catch (e) { console.error("daily_rates:", e.message); }
  };
  const fetchPurchases = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/purchase_details.php`); setPurchaseData(r.data?.data || []); }
    catch (e) { console.error("purchases:", e.message); }
  };
  const fetchRetailers = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/retailer_details.php`); setRetailerData(r.data?.data || []); }
    catch (e) { console.error("retailers:", e.message); }
  };
  const fetchOrders = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/orders.php`); setOrdersData(r.data?.data || []); }
    catch (e) { console.error("orders:", e.message); }
  };
  const fetchSales = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/sales.php`); setSalesData(r.data?.data || []); }
    catch (e) { console.error("sales:", e.message); }
  };
  const fetchChitList = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/chit_list.php`); setChitListData(r.data?.data || []); }
    catch (e) { console.error("chit_list:", e.message); }
  };
  const fetchProducts = async () => {
    try {
      const r = await axios.get(`${ApiUrl}/Api/product.php`);
      setProductsData((r.data?.data || []).sort((a, b) => a.id - b.id));
    }
    catch (e) { console.error("products:", e.message); }
  };
  const fetchCustomers = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/customer.php`); setCustomersData(r.data?.data || []); }
    catch (e) { console.error("customers:", e.message); }
  };
  const fetchSuppliers = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/supplier.php`); setSuppliersData(r.data?.data || []); }
    catch (e) { console.error("suppliers:", e.message); }
  };
  const fetchLoans = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/loan.php`); setLoansData(r.data?.data || []); }
    catch (e) { console.error("loans:", e.message); }
  };
  const fetchStock = async () => {
    try { const r = await axios.get(`${ApiUrl}/Api/stock.php`); setStockData(r.data?.data || []); }
    catch (e) { console.error("stock:", e.message); }
  };

  // ── Utils ──────────────────────────────────────────────────────────────────
  const fmt12 = (t) => {
    if (!t) return "N/A";
    try {
      const d = new Date(`1970-01-01T${t}`);
      if (!isNaN(d)) return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
      const p = t.split(":");
      if (p.length >= 2) { let h = parseInt(p[0]); const am = h >= 12 ? "PM" : "AM"; h = h % 12 || 12; return `${h}:${p[1]} ${am}`; }
    } catch { }
    return "N/A";
  };

  const parseDate = (s) => {
    if (!s) return null;
    if (s.includes("-")) {
      const p = s.split("-");
      if (p.length === 3) {
        let d = new Date(`${p[2]}-${p[1]}-${p[0]}`);
        if (!isNaN(d)) return d;
        d = new Date(s);
        if (!isNaN(d)) return d;
      }
    }
    const d = new Date(s);
    return isNaN(d) ? null : d;
  };

  const deepSearch = (obj, lower) => {
    if (!obj) return false;
    return Object.values(obj).some(v =>
      typeof v === "object" && v !== null ? deepSearch(v, lower) : (v?.toString().toLowerCase() || "").includes(lower)
    );
  };

  const getLoanPaid = (item) => {
    const hist = item.loan_payment_history || [];
    if (hist.length) return hist.reduce((s, p) => s + parseFloat(p.amount_paid || 0), 0);
    return parseFloat(item.paid || 0);
  };
  const getLoanBalance = (item) => Math.max(0, parseFloat(item.loan_amount || 0) - getLoanPaid(item));

  const rupee = (v) => v != null && v !== "" ? `₹${parseFloat(v).toFixed(2)}` : "N/A";

  // ── Raw data accessor ──────────────────────────────────────────────────────
  const getRaw = () => {
    const map = {
      "DailyRates Report": dailyRatesData,
      "Purchases Report": purchaseData,
      "Retailers Report": retailerData,
      "Orders Report": ordersData,
      "Sales Report": salesData,
      "Chit Report": chitListData,
      "Products Report": productsData,
      "Customers Report": customersData,
      "Suppliers Report": suppliersData,
      "Loans Report": loansData,
      "Stock Report": stockData,
    };
    return map[selectedReport] || [];
  };

  // ── Unique option helpers ──────────────────────────────────────────────────
  const uniq = (fn) => { const s = new Set(); getRaw().forEach(fn(s)); return Array.from(s).sort(); };
  const uniqueManagers = () => uniq(s => item => { const v = item.manager_name || item.manager; if (v) s.add(v); });
  const uniqueStaff = () => uniq(s => item => { if (item.staff) s.add(item.staff); });
  const uniqueStatuses = () => uniq(s => item => { const v = item.delivery_status || item.loan_status || item.status; if (v) s.add(v); });
  const uniqueCustomers = () => {
    const m = new Map();
    getRaw().forEach(item => {
      const id = item.customer_id || item.customer?.customer_id;
      const name = item.customer_info?.name || item.customer_data?.name || item.customer?.name || item.customer_name || item.name || id;
      if (id) m.set(id, name);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  };
  const uniqueSuppliers = () => {
    const m = new Map();
    getRaw().forEach(item => {
      const id = item.supplier_id || item.supplier?.supplier_id;
      const name = item.supplier_details?.name || item.supplier?.name || item.supplier_name || id;
      if (id) m.set(id, name);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  };
  const uniqueRetailers = () => {
    const m = new Map();
    getRaw().forEach(item => {
      const id = item.supplier_id || item.retailer_details?.retailer_id || "";
      const name = item.retailer_details?.name || "";
      if (id) m.set(id, name || id);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  };

  // ── Apply filters ──────────────────────────────────────────────────────────
  const applyFilters = () => {
    const data = getRaw();
    const lower = searchTerm.toLowerCase();

    const filtered = data.filter(item => {
      let itemDate = null;
      if (selectedReport === "Retailers Report") itemDate = parseDate(item.retailer_date);
      else if (selectedReport === "Orders Report") itemDate = parseDate(item.delivery_date);
      else if (selectedReport === "Sales Report") itemDate = parseDate(item.sales_date);
      else if (selectedReport === "Chit Report") itemDate = parseDate(item.start_date);
      else if (selectedReport === "Loans Report") itemDate = parseDate(item.loan_date);
      else itemDate = parseDate(item.date || item.purchase_date);

      const from = fromDate ? new Date(fromDate) : null;
      const to = toDate ? new Date(new Date(toDate).setHours(23, 59, 59, 999)) : null;

      if (!(!searchTerm || deepSearch(item, lower))) return false;
      if (from && (!itemDate || itemDate < from)) return false;
      if (to && (!itemDate || itemDate > to)) return false;

      if (selectedManager) {
        const v = (item.manager_name || item.manager || "").toLowerCase();
        if (v !== selectedManager.toLowerCase()) return false;
      }
      if (selectedStaff) {
        if ((item.staff || "").toLowerCase() !== selectedStaff.toLowerCase()) return false;
      }

      if (selectedStatus) {
        if (selectedReport === "Stock Report") {
          if ((item.category || "").toLowerCase() !== selectedStatus.toLowerCase()) return false;
        } else {
          const v = (item.delivery_status || item.loan_status || item.status || "").toLowerCase();
          if (v !== selectedStatus.toLowerCase()) return false;
        }
      }

      if (selectedCustomer) {
        const id = item.customer_id || item.customer?.customer_id || "";
        if (id !== selectedCustomer) return false;
      }

      if (selectedSupplier) {
        const id = item.supplier_id || item.supplier?.supplier_id || "";
        if (id !== selectedSupplier) return false;
      }

      if (selectedRetailer) {
        const id = item.supplier_id || item.retailer_details?.retailer_id || "";
        if (id !== selectedRetailer) return false;
      }

      if (paymentFilter) {
        if (selectedReport === "Sales Report") {
          const bal = parseFloat(item.balance || 0);
          if (paymentFilter === "paid" && bal !== 0) return false;
          if (paymentFilter === "balance" && bal === 0) return false;
        }
        if (selectedReport === "Loans Report") {
          const bal = getLoanBalance(item);
          if (paymentFilter === "paid" && bal !== 0) return false;
          if (paymentFilter === "balance" && bal === 0) return false;
        }
      }

      return true;
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  };

  // ── Clear filters ──────────────────────────────────────────────────────────
  const clearFilters = (resetReport = true) => {
    if (resetReport) setSelectedReport("DailyRates Report");
    setSearchTerm("");
    setFromDate("");
    setToDate("");
    setSelectedManager("");
    setSelectedStaff("");
    setSelectedStatus("");
    setSelectedCustomer("");
    setSelectedSupplier("");
    setPaymentFilter("");
    setSelectedRetailer("");
    setCurrentPage(1);
    setShowAllItems(false);
    setItemsPerPage(10);
  };

  // ── Pagination ─────────────────────────────────────────────────────────────
  const totalItems = filteredData.length;
  const totalPages = showAllItems ? 1 : Math.ceil(totalItems / itemsPerPage);
  const currentItems = showAllItems
    ? filteredData
    : filteredData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (p) => { if (p >= 1 && p <= totalPages) setCurrentPage(p); };
  const handleItemsChange = (e) => {
    const v = e.target.value;
    if (v === "All") { setShowAllItems(true); setCurrentPage(1); }
    else { setShowAllItems(false); setItemsPerPage(Number(v)); setCurrentPage(1); }
  };

  const renderPages = () => {
    const pages = []; const max = 2;
    let s = Math.max(1, currentPage - Math.floor(max / 2));
    let e = Math.min(totalPages, s + max - 1);
    if (e - s + 1 < max) s = Math.max(1, e - max + 1);
    if (s > 1) { pages.push(<li key={1} className={`page-item ${currentPage === 1 ? "active" : ""}`}><button className="page-link" onClick={() => handlePageChange(1)}>1</button></li>); if (s > 2) pages.push(<li key="se" className="page-item disabled"><span className="page-link">…</span></li>); }
    for (let i = s; i <= e; i++) pages.push(<li key={i} className={`page-item ${currentPage === i ? "active" : ""}`}><button className="page-link" onClick={() => handlePageChange(i)}>{i}</button></li>);
    if (e < totalPages) { if (e < totalPages - 1) pages.push(<li key="ee" className="page-item disabled"><span className="page-link">…</span></li>); pages.push(<li key={totalPages} className={`page-item ${currentPage === totalPages ? "active" : ""}`}><button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button></li>); }
    return pages;
  };

  const handlePrint = () => { document.body.classList.add("print-table-only"); window.print(); document.body.classList.remove("print-table-only"); };

  // ── Table headers ──────────────────────────────────────────────────────────
  const getHeaders = () => ({
    "DailyRates Report": ["S.No","Date","Gold 1g Rate","Gold Coin Rate","Gold Pure %","Silver 1g Rate","Silver Coin Rate","Silver Pure %","Last Update"],
    "Purchases Report": ["S.No","Purchase ID","Purchase Date","Supplier ID","Supplier Name","Return Type","Paid","Total Amount","Total Pure","Items"],
    "Retailers Report": ["S.No","Retailer No","Retailer ID","Retailer Date","Retailer Name","Mobile","Address","Return Type","Opening (g)","Current (g)","Total Pure (g)","Paid / Pure Returned","Closing (g)","Total Items","Manager","Staff"],
    "Orders Report": ["S.No","Order No","Customer ID","Customer Name","Manager","Staff","Category","Delivery Date","Status","Total Amount"],
    "Sales Report": ["S.No","Sales No","Sales Date","Customer ID","Customer Name","Manager","Staff","Category","Total","Paid","Balance"],
    "Chit Report": ["S.No","Chit Id","Customer ID","Customer Name","Start Date","Duration","Status","Manager","Staff"],
    "Products Report": ["S.No","Product ID","Item Code","Item Name","Category","Type","Purity %","Status"],
    "Customers Report": ["S.No","Customer ID","Customer Type","Name","Mobile","Address","Status"],
    "Suppliers Report": ["S.No","Supplier ID","Name","Mobile","Address","Status"],
    "Loans Report": ["S.No","Loan ID","Customer ID","Customer Name","Loan Date","Loan Amount","Paid","Balance","Status","Manager","Staff"],
    "Stock Report": ["S.No","Item","Category","Total Qty","Sold Qty","Available Qty","Total Weight","Last Updated"],
  }[selectedReport] || []);

  // ── Row renderer ───────────────────────────────────────────────────────────
  const renderRow = (item) => {
    const badge = (v, ok = "active") => (
      <span className={`badge ${(v || "").toLowerCase() === ok.toLowerCase() ? "bg-success" : "bg-secondary"}`}>{v || "N/A"}</span>
    );

    if (selectedReport === "DailyRates Report") {
      return (
        <>
          <td>{item.date || "N/A"}</td>
          <td>{item.gold_1g_rate ? rupee(item.gold_1g_rate) : "N/A"}</td>
          <td>{item.cold_coin_rate ? rupee(item.cold_coin_rate) : "N/A"}</td>
          <td>{item.gold_pure_percentage != null ? `${item.gold_pure_percentage}%` : "N/A"}</td>
          <td>{item.silver_10g_rate ? rupee(item.silver_10g_rate) : "N/A"}</td>
          <td>{item.silver_coin_rate ? rupee(item.silver_coin_rate) : "N/A"}</td>
          <td>{item.silver_pure_percentage != null ? `${item.silver_pure_percentage}%` : "N/A"}</td>
          <td>{fmt12(item.time)}</td>
        </>
      );
    }

    if (selectedReport === "Purchases Report") {
      return (
        <>
          <td>{item.purchase_id || "N/A"}</td>
          <td>{item.purchase_date || "N/A"}</td>
          <td>{item.supplier_id || "N/A"}</td>
          <td>{item.supplier_details?.name || item.supplier?.name || item.supplier_name || "N/A"}</td>
          <td>{item.return_type || "N/A"}</td>
          <td>{item.paid != null ? rupee(item.paid) : "N/A"}</td>
          <td>{item.opening != null ? rupee(item.opening) : "N/A"}</td>
          <td>{item.total_pure || item.pure || "N/A"}</td>
          <td>{item.items?.length ?? "N/A"}</td>
        </>
      );
    }

    if (selectedReport === "Retailers Report") {
      return (
        <>
          <td>{item.retailer_id || "N/A"}</td>
          <td>{item.supplier_id || "N/A"}</td>
          <td>{item.retailer_date || "N/A"}</td>
          <td>{item.retailer_details?.name || "N/A"}</td>
          <td>{item.retailer_details?.mobile || "N/A"}</td>
          <td>{item.retailer_details?.address || "N/A"}</td>
          <td>{item.return_type || "N/A"}</td>
          <td>{item.opening ?? "N/A"}</td>
          <td>{item.current ?? "N/A"}</td>
          <td>{item.total_pure ?? "N/A"}</td>
          <td>
            {item.return_type === "Pure"
              ? `${item.pure ?? 0} g`
              : item.return_type === "Amount"
              ? `₹${item.enter_amount ?? 0}`
              : "N/A"}
          </td>
          <td>{item.close ?? "N/A"}</td>
          <td>{item.items?.length ?? 0}</td>
          <td>{item.manager_name || item.manager || "N/A"}</td>
          <td>{item.staff || "N/A"}</td>
        </>
      );
    }

    if (selectedReport === "Orders Report") {
      return (
        <>
          <td>{item.order_no || "N/A"}</td>
          <td>{item.customer_id || item.customer_info?.customer_id || "N/A"}</td>
          <td>{item.customer_info?.name || "N/A"}</td>
          <td>{item.manager_name || item.manager || "N/A"}</td>
          <td>{item.staff || "N/A"}</td>
          <td>{item.order_items?.[0]?.category || "N/A"}</td>
          <td>{item.delivery_date || "N/A"}</td>
          <td>
            <span className={`badge ${
              (item.delivery_status || "").toLowerCase() === "delivered" ? "bg-success" :
              (item.delivery_status || "").toLowerCase() === "confirmed" ? "bg-primary" : "bg-warning text-dark"
            }`}>{item.delivery_status || "N/A"}</span>
          </td>
          <td>{item.total ? rupee(item.total) : "N/A"}</td>
        </>
      );
    }

    if (selectedReport === "Sales Report") {
      return (
        <>
          <td>{item.sales_no || "N/A"}</td>
          <td>{item.sales_date || "N/A"}</td>
          <td>{item.customer_id || "N/A"}</td>
          <td>{item.customer_info?.name || item.customer_data?.name || item.customer?.name || item.customer_name || "N/A"}</td>
          <td>{item.manager_name || item.manager || "N/A"}</td>
          <td>{item.staff || "N/A"}</td>
          <td>{item.item_list?.[0]?.category || item.category || "N/A"}</td>
          <td>{item.total ? rupee(item.total) : "N/A"}</td>
          <td>{item.paid != null ? rupee(item.paid) : "N/A"}</td>
          <td>{item.balance != null ? rupee(item.balance) : "N/A"}</td>
        </>
      );
    }

    if (selectedReport === "Chit Report") {
      return (
        <>
          <td>{item.chit_id || "N/A"}</td>
          <td>{item.customer_id || "N/A"}</td>
          <td>{item.name || item.customer?.name || "N/A"}</td>
          <td>{item.start_date || "N/A"}</td>
          <td>{item.duration || "N/A"}</td>
          <td>{badge(item.status)}</td>
          <td>{item.manager_name || item.manager || "N/A"}</td>
          <td>{item.staff || "N/A"}</td>
        </>
      );
    }

    if (selectedReport === "Products Report") {
      return (
        <>
          <td>{item.id || "N/A"}</td>
          <td>{item.item_code || "N/A"}</td>
          <td>{item.item_name || "N/A"}</td>
          <td>{item.category || "N/A"}</td>
          <td>{item.type || "N/A"}</td>
          <td>{item.pure_percent != null && item.pure_percent !== "" ? `${item.pure_percent}%` : "N/A"}</td>
          <td>{badge(item.status)}</td>
        </>
      );
    }

    if (selectedReport === "Customers Report") {
      return (
        <>
          <td>{item.customer_id || "N/A"}</td>
          <td>{item.customer_type || "N/A"}</td>
          <td>{item.name || "N/A"}</td>
          <td>{item.mobile || "N/A"}</td>
          <td>{item.address || "N/A"}</td>
          <td>{badge(item.status)}</td>
        </>
      );
    }

    if (selectedReport === "Suppliers Report") {
      return (
        <>
          <td>{item.supplier_id || "N/A"}</td>
          <td>{item.name || "N/A"}</td>
          <td>{item.mobile || "N/A"}</td>
          <td>{item.address || "N/A"}</td>
          <td>{badge(item.status)}</td>
        </>
      );
    }

    if (selectedReport === "Loans Report") {
      const paid = getLoanPaid(item);
      const balance = getLoanBalance(item);
      return (
        <>
          <td>{item.loan_id || "N/A"}</td>
          <td>{item.customer_id || "N/A"}</td>
          <td>{item.customer?.name || item.customer_name || "N/A"}</td>
          <td>{item.loan_date || "N/A"}</td>
          <td>{item.loan_amount ? rupee(item.loan_amount) : "N/A"}</td>
          <td>{rupee(paid)}</td>
          <td>{rupee(balance)}</td>
          <td>
            <span className={`badge ${balance === 0 ? "bg-success" : "bg-warning text-dark"}`}>
              {balance === 0 ? "Paid" : "Pending"}
            </span>
          </td>
          <td>{item.manager_name || item.manager || "N/A"}</td>
          <td>{item.staff || "N/A"}</td>
        </>
      );
    }

    if (selectedReport === "Stock Report") {
      const categoryBadge = (cat) => {
        const c = (cat || "").toLowerCase();
        return (
          <span className={`badge ${c === "gold" ? "bg-warning text-dark" : c === "silver" ? "bg-secondary" : "bg-info text-dark"}`}>
            {cat || "N/A"}
          </span>
        );
      };
      return (
        <>
          <td>{item.item || item.item_name_english || "N/A"}</td>
          <td>{categoryBadge(item.category)}</td>
          <td>{item.total_quantity ?? "N/A"}</td>
          <td>{item.sold_quantity ?? "N/A"}</td>
          <td>{item.avaliable_quantity ?? "N/A"}</td>
          <td>{item.weight || "N/A"}</td>
          <td>{item.last_updated || "N/A"}</td>
        </>
      );
    }

    return null;
  };

  // ── Export row builder ─────────────────────────────────────────────────────
  const buildExportRow = (item, i) => {
    const sno = { "S.No": i + 1 };
    if (selectedReport === "DailyRates Report") return { ...sno,
      "Date": item.date || "N/A",
      "Gold 1g Rate": item.gold_1g_rate ? rupee(item.gold_1g_rate) : "N/A",
      "Gold Coin Rate": item.cold_coin_rate ? rupee(item.cold_coin_rate) : "N/A",
      "Gold Pure %": item.gold_pure_percentage != null ? `${item.gold_pure_percentage}%` : "N/A",
      "Silver 1g Rate": item.silver_10g_rate ? rupee(item.silver_10g_rate) : "N/A",
      "Silver Coin Rate": item.silver_coin_rate ? rupee(item.silver_coin_rate) : "N/A",
      "Silver Pure %": item.silver_pure_percentage != null ? `${item.silver_pure_percentage}%` : "N/A",
      "Last Update": fmt12(item.time),
    };
    if (selectedReport === "Purchases Report") return { ...sno,
      "Purchase ID": item.purchase_id || "N/A",
      "Purchase Date": item.purchase_date || "N/A",
      "Supplier ID": item.supplier_id || "N/A",
      "Supplier Name": item.supplier_details?.name || item.supplier?.name || item.supplier_name || "N/A",
      "Return Type": item.return_type || "N/A",
      "Paid": item.paid != null ? rupee(item.paid) : "N/A",
      "Total Amount": item.opening != null ? rupee(item.opening) : "N/A",
      "Total Pure": item.total_pure || item.pure || "N/A",
      "Items": item.items?.length ?? "N/A",
    };
    if (selectedReport === "Retailers Report") return { ...sno,
      "Retailer No": item.retailer_id || "N/A",
      "Retailer ID": item.supplier_id || "N/A",
      "Retailer Date": item.retailer_date || "N/A",
      "Retailer Name": item.retailer_details?.name || "N/A",
      "Manager": item.manager_name || item.manager || "N/A",
      "Staff": item.staff || "N/A",
      "Total Items": item.items?.length ?? 0,
      "Total Pure": item.total_pure || "N/A",
    };
    if (selectedReport === "Orders Report") return { ...sno,
      "Order No": item.order_no || "N/A",
      "Customer ID": item.customer_id || item.customer_info?.customer_id || "N/A",
      "Customer Name": item.customer_info?.name || "N/A",
      "Manager": item.manager_name || item.manager || "N/A",
      "Staff": item.staff || "N/A",
      "Category": item.order_items?.[0]?.category || "N/A",
      "Delivery Date": item.delivery_date || "N/A",
      "Status": item.delivery_status || "N/A",
      "Total Amount": item.total ? rupee(item.total) : "N/A",
    };
    if (selectedReport === "Sales Report") return { ...sno,
      "Sales No": item.sales_no || "N/A",
      "Sales Date": item.sales_date || "N/A",
      "Customer ID": item.customer_id || "N/A",
      "Customer Name": item.customer_info?.name || item.customer_data?.name || item.customer?.name || item.customer_name || "N/A",
      "Manager": item.manager_name || item.manager || "N/A",
      "Staff": item.staff || "N/A",
      "Category": item.item_list?.[0]?.category || item.category || "N/A",
      "Total": item.total ? rupee(item.total) : "N/A",
      "Paid": item.paid != null ? rupee(item.paid) : "N/A",
      "Balance": item.balance != null ? rupee(item.balance) : "N/A",
    };
    if (selectedReport === "Chit Report") return { ...sno,
      "Chit Id": item.chit_id || "N/A",
      "Customer ID": item.customer_id || "N/A",
      "Customer Name": item.name || item.customer?.name || "N/A",
      "Start Date": item.start_date || "N/A",
      "Duration": item.duration || "N/A",
      "Status": item.status || "N/A",
      "Manager": item.manager_name || item.manager || "N/A",
      "Staff": item.staff || "N/A",
    };
    if (selectedReport === "Products Report") return { ...sno,
      "Product ID": item.id || "N/A",
      "Item Code": item.item_code || "N/A",
      "Item Name": item.item_name || "N/A",
      "Category": item.category || "N/A",
      "Type": item.type || "N/A",
      "Purity %": item.pure_percent != null && item.pure_percent !== "" ? `${item.pure_percent}%` : "N/A",
      "Status": item.status || "N/A",
    };
    if (selectedReport === "Customers Report") return { ...sno,
      "Customer ID": item.customer_id || "N/A",
      "Customer Type": item.customer_type || "N/A",
      "Name": item.name || "N/A",
      "Mobile": item.mobile || "N/A",
      "Address": item.address || "N/A",
      "Status": item.status || "N/A",
    };
    if (selectedReport === "Suppliers Report") return { ...sno,
      "Supplier ID": item.supplier_id || "N/A",
      "Name": item.name || "N/A",
      "Mobile": item.mobile || "N/A",
      "Address": item.address || "N/A",
      "Status": item.status || "N/A",
    };
    if (selectedReport === "Loans Report") {
      const paid = getLoanPaid(item);
      const balance = getLoanBalance(item);
      return { ...sno,
        "Loan ID": item.loan_id || "N/A",
        "Customer ID": item.customer_id || "N/A",
        "Customer Name": item.customer?.name || item.customer_name || "N/A",
        "Loan Date": item.loan_date || "N/A",
        "Loan Amount": item.loan_amount ? rupee(item.loan_amount) : "N/A",
        "Paid": rupee(paid),
        "Balance": rupee(balance),
        "Status": balance === 0 ? "Paid" : "Pending",
        "Manager": item.manager_name || item.manager || "N/A",
        "Staff": item.staff || "N/A",
      };
    }
    if (selectedReport === "Stock Report") return { ...sno,
      "Item": item.item || item.item_name_english || "N/A",
      "Category": item.category || "N/A",
      "Total Qty": item.total_quantity ?? "N/A",
      "Sold Qty": item.sold_quantity ?? "N/A",
      "Available Qty": item.avaliable_quantity ?? "N/A",
      "Total Weight": item.weight || "N/A",
      "Last Updated": item.last_updated || "N/A",
    };
    return {};
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(12);
      doc.text(selectedReport, 14, 15);
      autoTable(doc, {
        head: [getHeaders()],
        body: filteredData.map((item, i) => Object.values(buildExportRow(item, i))),
        startY: 22,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [133, 193, 233] },
      });
      doc.save(`${selectedReport.replace(/ /g, "_")}.pdf`);
    } catch (e) { console.error("PDF error:", e); }
  };

  const exportToExcel = () => {
    try {
      const ws = XLSX.utils.json_to_sheet(filteredData.map((item, i) => buildExportRow(item, i)));
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, selectedReport.slice(0, 31));
      XLSX.writeFile(wb, `${selectedReport.replace(/ /g, "_")}.xlsx`);
    } catch (e) { console.error("Excel error:", e); }
  };

  const csvData = filteredData.map((item, i) => buildExportRow(item, i));

  const supportsManagerStaff = () =>
    ["Retailers Report","Orders Report","Sales Report","Chit Report","Loans Report"].includes(selectedReport);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="container-fluid py-3">
      <style>{`
        @media print {
          body.print-table-only * { visibility: hidden; }
          body.print-table-only .table-responsive,
          body.print-table-only .table-responsive * { visibility: visible; }
          body.print-table-only .table-responsive { position: absolute; top: 0; left: 0; width: 100%; }
          body.print-table-only .table { width: 100%; border-collapse: collapse; }
          body.print-table-only .table th,
          body.print-table-only .table td { border: 1px solid #000; padding: 6px; font-size: 10px; }
          body.print-table-only .table thead th { background-color: #85C1E9 !important; }
        }
      `}</style>

      {/* ── Filter Card ── */}
      <div className="card card-body mb-3">
        {/* Row 1: Report selector + Date range + Search + Reset/Apply */}
        <div className="row g-2">
          <div className="col-lg-2 col-md-4">
            <label className="form-label fw-semibold mb-1 small">Report Type</label>
            <select className="form-select" value={selectedReport}
              onChange={(e) => { setSelectedReport(e.target.value); }}>
              <option value="DailyRates Report">DailyRates Report</option>
              <option value="Purchases Report">Purchases Report</option>
              <option value="Retailers Report">Retailers Report</option>
              <option value="Orders Report">Orders Report</option>
              <option value="Sales Report">Sales Report</option>
              <option value="Chit Report">Chit Report</option>
              <option value="Products Report">Products Report</option>
              <option value="Customers Report">Customers Report</option>
              <option value="Suppliers Report">Suppliers Report</option>
              <option value="Loans Report">Loans Report</option>
              <option value="Stock Report">Stock Report</option>
            </select>
          </div>
          <div className="col-lg-2 col-md-3">
            <label className="form-label fw-semibold mb-1 small">From Date</label>
            <input type="date" className="form-control" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div className="col-lg-2 col-md-3">
            <label className="form-label fw-semibold mb-1 small">To Date</label>
            <input type="date" className="form-control" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div className="col-lg-2 col-md-4">
            <label className="form-label fw-semibold mb-1 small">Search</label>
            <input
              type="search"
              className="form-control"
              placeholder="Search..."
              defaultValue={searchTerm}
              onChange={(e) => debouncedSetSearch(e.target.value)}
            />
          </div>
          <div className="col-lg-4 col-md-6 d-flex gap-2">
            <button className="btn btn-secondary btn-sm h-50 mt-4" onClick={() => clearFilters(false)}>Reset</button>
            <button className="btn btn-primary btn-sm h-50 mt-4" onClick={applyFilters}>Apply</button>
          </div>
        </div>

        {/* Row 2: Conditional filters */}
        <div className="row g-2 mt-2 align-items-end">
          {/* Stock category filter */}
          {selectedReport === "Stock Report" && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label fw-semibold mb-1 small">Category</label>
              <select className="form-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="">All Categories</option>
                <option value="gold">Gold</option>
                <option value="silver">Silver</option>
              </select>
            </div>
          )}

          {/* Order status */}
          {selectedReport === "Orders Report" && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label fw-semibold mb-1 small">Order Status</label>
              <select className="form-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="inprogress">In Progress</option>
                <option value="confirmed">Confirmed</option>
                <option value="delivered">Delivered</option>
              </select>
            </div>
          )}

          {/* Chit status */}
          {selectedReport === "Chit Report" && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label fw-semibold mb-1 small">Chit Status</label>
              <select className="form-select" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                <option value="">All Status</option>
                {uniqueStatuses().map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}

          {/* Sales payment */}
          {selectedReport === "Sales Report" && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label fw-semibold mb-1 small">Payment</label>
              <select className="form-select" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
                <option value="">All</option>
                <option value="paid">Fully Paid</option>
                <option value="balance">Has Balance</option>
              </select>
            </div>
          )}

          {/* Loan payment */}
          {selectedReport === "Loans Report" && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label fw-semibold mb-1 small">Payment Status</label>
              <select className="form-select" value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
                <option value="">All</option>
                <option value="paid">Paid</option>
                <option value="balance">Pending</option>
              </select>
            </div>
          )}

          {/* Customer filter */}
          {["Sales Report", "Loans Report", "Chit Report"].includes(selectedReport) && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label fw-semibold mb-1 small">Customer</label>
              <select className="form-select" value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)}>
                <option value="">All Customers</option>
                {uniqueCustomers().map(([id, name]) => (
                  <option key={id} value={id}>{id} – {name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Retailer filter - Shows ID - Name */}
          {selectedReport === "Retailers Report" && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label fw-semibold mb-1 small">Retailer</label>
              <select className="form-select" value={selectedRetailer} onChange={(e) => setSelectedRetailer(e.target.value)}>
                <option value="">All Retailers</option>
                {uniqueRetailers().map(([id, name]) => (
                  <option key={id} value={id}>{id} - {name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Supplier filter */}
          {selectedReport === "Purchases Report" && (
            <div className="col-lg-2 col-md-3">
              <label className="form-label fw-semibold mb-1 small">Supplier</label>
              <select className="form-select" value={selectedSupplier} onChange={(e) => setSelectedSupplier(e.target.value)}>
                <option value="">All Suppliers</option>
                {uniqueSuppliers().map(([id, name]) => (
                  <option key={id} value={id}>{id} – {name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Manager / Staff */}
          {supportsManagerStaff() && (
            <>
              <div className="col-lg-2 col-md-3">
                <label className="form-label fw-semibold mb-1 small">Manager</label>
                <select className="form-select" value={selectedManager} onChange={(e) => setSelectedManager(e.target.value)}>
                  <option value="">All Managers</option>
                  {uniqueManagers().map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-lg-2 col-md-3">
                <label className="form-label fw-semibold mb-1 small">Staff</label>
                <select className="form-select" value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)}>
                  <option value="">All Staff</option>
                  {uniqueStaff().map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── Data Table Card ── */}
      <div className="card mt-2 mb-5 shadow-sm border-0 rounded-3">
        <div className="card-body">
          {/* Table toolbar */}
          <div className="row align-items-center mb-3 g-2">
            <div className="col-md-2">
              <h5 className="mb-0 text-dark fw-semibold">{selectedReport}</h5>
              <small className="text-muted">{totalItems} record{totalItems !== 1 ? "s" : ""}</small>
            </div>
            <div className="col-md-1">
              <select className="form-select form-select-sm" value={showAllItems ? "All" : itemsPerPage} onChange={handleItemsChange}>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="All">All</option>
              </select>
            </div>
            <div className="col-md-9">
              <div className="d-flex gap-2 justify-content-end flex-wrap align-items-center">
                <button className="btn btn-danger btn-sm" onClick={exportToPDF}>
                  <i className="fa-solid fa-file-pdf me-1"></i>PDF
                </button>
                <button className="btn btn-success btn-sm" onClick={exportToExcel}>
                  <i className="fas fa-file-excel me-1"></i>Excel
                </button>
                <CSVLink data={csvData} filename={`${selectedReport.replace(/ /g, "_")}.csv`} className="btn btn-primary btn-sm">
                  <i className="fas fa-file-csv me-1"></i>CSV
                </CSVLink>
                <button className="btn btn-secondary btn-sm" onClick={handlePrint}>
                  <i className="fas fa-print me-1"></i>Print
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive card">
            <table className="table table-hover mb-0">
              <thead>
                <tr>
                  {getHeaders().map((title, i) => (
                    <th key={i} style={{ background: "#85C1E9", fontSize: "13px", whiteSpace: "nowrap" }}>
                      {title}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={item.id || item.loan_id || item.chit_id || item.sales_no || index}>
                      <td>{showAllItems ? index + 1 : (currentPage - 1) * itemsPerPage + index + 1}</td>
                      {renderRow(item)}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={getHeaders().length} className="text-center py-5 text-muted">
                      <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="row mt-3 align-items-center">
            <div className="col-lg-5">
              <p className="text-muted small mb-0">
                Showing {totalItems === 0 ? 0 : showAllItems ? 1 : (currentPage - 1) * itemsPerPage + 1}–{showAllItems ? totalItems : Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
              </p>
            </div>
            <div className="col-lg-7">
              <nav>
                <ul className="pagination justify-content-end mb-0">
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(1)}>««</button>
                  </li>
                  <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(currentPage - 1)}>«</button>
                  </li>
                  {renderPages()}
                  <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(currentPage + 1)}>»</button>
                  </li>
                  <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(totalPages)}>»»</button>
                  </li>
                </ul>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Report;